import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { Button } from '../atoms/Button/Button';
import { ProviderField } from '../../models/provider';
import {
  ProviderInputs,
  SubmitProviderInputsPayload,
} from '../screens/SubmitCredentials/molecules/ProviderInputs/ProviderInputs';
import { ContentBody } from '../atoms/ContentBody/ContentBody';
import { ContentAlignmentContainer } from '../ContentAlignmentContainer/ContentAlignmentContainer';
import { TextAlignmentContainer } from '../TextAlignmentContainer/TextAlignmentContainer';
import { H1 } from '../atoms/Heading/Heading';
import { Text } from '../atoms/Text/Text';
import { useCredentialsCopy } from '../screens/SubmitCredentials/hooks/useCredentialsCopy';
import { Footer } from '../atoms/Footer/Footer';
import { Accordion } from '../atoms/Accordion/Accordion';
import { messages } from './messages';
import { TemplateIcon } from './TemplateIcon';
import { TemplateOrderedList } from './molecules/TemplateOrderedList/TemplateOrderedList';
import { TemplateElementStyle, TemplateElementType, Templates } from './templates';
import { MaybeChangeMethodButton } from './MaybeChangeMethodButton/MaybeChangeMethodButton';

export type AppCodeTemplateViewProps = {
  fields: ProviderField[];
  onSubmit: (values: SubmitProviderInputsPayload) => void;
  /**
   * Callback for toggling a child Accordion component.
   * It can be an ORDERED_LIST field, or an accordion like "Why authenticate twice"
   */
  onToggle?: (fieldName: string) => void;
};

export const AppCodeTemplateView: React.FC<AppCodeTemplateViewProps> = ({
  fields,
  onSubmit,
  onToggle,
}) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);

  const parsedElements = useMemo(() => {
    const icon = fields.find((el) => el.type === TemplateElementType.ICON);
    const input = fields.find((el) => el.type === TemplateElementType.INPUT);
    const title = fields.find(
      (el) => el.type === TemplateElementType.TEXT && el.style === TemplateElementStyle.TITLE
    );
    const orderedList = fields.find(
      (el) => el.type === TemplateElementType.TEXT && el.style === TemplateElementStyle.ORDERED_LIST
    );

    return { icon, inputs: input ? [input] : [], title, orderedList };
  }, [fields]);

  const { heading } = useCredentialsCopy();

  return (
    <ContentBody data-testid="app-code-template-view">
      <Box>
        <H1 mb="xxsmall">{heading}</H1>

        {parsedElements.title && (
          <TextAlignmentContainer>
            <Text color="secondary" mb="medium">
              {parsedElements.title.value}
            </Text>
          </TextAlignmentContainer>
        )}

        {parsedElements.icon && (
          <Box mb="xxsmall">
            <ContentAlignmentContainer>
              <TemplateIcon height="xlarge" field={parsedElements.icon} />
            </ContentAlignmentContainer>
          </Box>
        )}

        <ProviderInputs
          formId={Templates.APP_CODE}
          providerFields={parsedElements.inputs}
          onSubmit={onSubmit}
          setButtonDisabled={setIsButtonDisabled}
        />

        {parsedElements.orderedList && (
          <Box mt="normedium" mb="normal">
            <Accordion
              heading={parsedElements.orderedList.description}
              name="instructions"
              onToggle={onToggle}
            >
              <TemplateOrderedList field={parsedElements.orderedList} renderDescription={false} />
            </Accordion>
          </Box>
        )}
      </Box>
      <Footer stickToBottom px="none">
        <Button
          mb="normal"
          form={Templates.APP_CODE}
          disabled={isButtonDisabled}
          size="large"
          type="submit"
          color="primary"
          variant="contained"
        >
          <FormattedMessage {...messages.idCompletionSubmitButton} />
        </Button>
        <MaybeChangeMethodButton elements={fields} onSubmit={onSubmit} />
      </Footer>
    </ContentBody>
  );
};
